import type { UseQueryResult } from "@tanstack/react-query";
import type { ColumnDef } from "@tanstack/react-table";

import { Button, DatePicker, ErrorContainer, FormInput, Table, TableActionsDropdown } from "@sentricmusic/ui";
import { useNavigate } from "@tanstack/react-router";
import { format } from "date-fns";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import type {
  Setlist,
} from "@/api/queries";

type AllSetlistsProps = {
  setlists: UseQueryResult<Setlist[], Error>;
};

function AllSetlistsTable({ setlists }: AllSetlistsProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [name, setName] = useState("");
  const [date, setDate] = useState<Date | undefined>(undefined);

  const handleClear = () => {
    setName("");
    setDate(undefined);
  };

  const columns = useMemo((): ColumnDef<Setlist>[] => [
    {
      header: String(t("gigs:table-header-name", "Name")),
      accessorKey: "name",
      size: 120,
      id: "colSetlistName",
    },
    {
      header: String(t("dashboard:ra-column-date", "Date")),
      accessorKey: "created",
      size: 120,
      id: "colSetlistDate",
      cell: ({ row }) => {
        const date = row.original.created as string | null | undefined;
        return (date != null && date !== "" && date !== "0000-00-00")
          ? date.split("T")[0]
          : String(t("gigs:unknown-date", "Unknown date"));
      },
    },
    {
      header: String(t("gigs:table-header-songs", "Songs")),
      accessorKey: "songs",
      size: 120,
      id: "colSetlistSongsCount",
      cell: ({ row }) => {
        try {
          return (JSON.parse(row.original.songs ?? "[]") as unknown[]).length;
        }
        catch {
          return 0;
        }
      },
    },
    {
      id: "actions",
      header: "",
      enableSorting: false,
      size: 60,
      minSize: 60,
      maxSize: 60,
      enablePinning: true,
      meta: { pinSide: "right" },
      cell: ({ row }) => {
        const goToSetlist = (event: React.MouseEvent): void => {
          event.stopPropagation();
          if (row.original.id) {
            void navigate({ to: `/gigs/setlists/update/${row.original.id}` });
          }
        };

        return (
          <TableActionsDropdown disabled={!row.original.id}>
            <Button
              variant="dropdown"
              label={String(t("common:edit", "Edit"))}
              onClick={goToSetlist}
            />
          </TableActionsDropdown>
        );
      },
    },
  ], [t, navigate]);

  const filteredSetlists = useMemo(() => {
    const setlistsData = setlists.data ?? [];

    const nameTerm = name.trim().toLowerCase();
    const dateStr = date ? format(date, "yyyy-MM-dd") : undefined;

    return setlistsData.filter((setlist) => {
      if (nameTerm && !setlist.name?.toLowerCase().includes(nameTerm)) {
        return false;
      }

      if (dateStr !== undefined) {
        const created = setlist.created as string | null | undefined;
        const createdDate = (created != null && created !== "" && created !== "0000-00-00")
          ? created.split("T")[0]
          : null;
        if (createdDate !== dateStr) {
          return false;
        }
      }

      return true;
    });
  }, [setlists.data, name, date]);

  const filterContent = (
    <>
      <FormInput
        label={t("gigs:filter-by-name", "Name")}
        smallFont
        placeholder={`${t("common:filter", "Filter")} ${t("common:setlists", "setlists")}`}
        value={name}
        onChange={e => setName(e.target.value)}
      />

      <DatePicker
        label={t("gigs:filter-by-date", "Date")}
        smallFont
        value={date}
        onChange={setDate}
        placeholder={String(t("gigs:filter-date-placeholder", "Pick a date"))}
      />
    </>
  );

  const totalSetlists = setlists.data?.length ?? 0;
  const filteredCount = filteredSetlists.length;

  const statsBlock = useMemo(() => [
    { title: t("gigs:stat-total-setlists", "Total setlists"), value: totalSetlists, highlight: false },
    { title: t("gigs:stat-filtered-setlists", "Filtered"), value: filteredCount, highlight: true },
  ], [totalSetlists, filteredCount, t]);

  return (
    <div>
      {setlists.isError
        ? (
            <ErrorContainer
              message={setlists.error?.message ?? t("common:errorGettingRecentSetlists", "Error getting recent setlists")}
            />
          )
        : (
            <div className="overflow-x-auto">
              <Table
                data={filteredSetlists}
                columns={columns}
                filterContent={filterContent}
                onClear={handleClear}
                statsBlock={statsBlock}
                isLoading={setlists.isLoading}
                isRefetchingData={setlists.isFetching}
                defaultPageSize={10}
              />
            </div>
          )}
    </div>
  );
}

export default AllSetlistsTable;
